import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {isMobile} from '#core/utils/context-utils.js';
import {merge, mergeTo} from '#core/utils/config-utils.js';
import {buildIntentsFromConfig} from '#core/utils/intent-factory.js';

import {ApplicationConfig} from '#core/config/application-config.js';

import {AbstractVisualFeature} from '#core/application/modules/visual-feature.js';
import {generateLinkMenuVerticalList} from '#core/application/modules/tiles/list-item/index.js';
import {CLOSE_BUTTON_TYPES, generateCloseButton} from '#core/application/modules/tiles/close-button/index.js';
import {generateSettingMenuGlobalStuff} from '#core/application/modules/tiles/settings/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generateFavoriteIcon} from '#core/application/modules/tiles/favorite-icon/index.js';

import {MenusLinksLists} from '../menus-links-list.js';

import SCHEMA from './az-menu.schema.yaml';
import PALETTE_SCHEMA from './az-menu.palette-schema.yaml';

class AZMenuFeature extends AbstractVisualFeature {
    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    get configSchema() {
        return SCHEMA;
    }

    getDesktopConfig(PALETTE) {
        const {DESKTOP_AZ_SPORTS_LIST} = PALETTE;

        const ITEMS_SEPARATOR_STYLE = DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR
            ? {
                  borderWidth: '0 0 1px 0',
                  borderColor: DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_SEPARATOR_COLOR,
                  borderStyle: 'solid',
              }
            : undefined;

        return {
            // Used for both desktop and mobile. And there could be totally different styles
            SportsMenuView: {
                separator: {
                    show: DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR,
                },
                container: {
                    style: {
                        ...resolveBackground(PALETTE.DESKTOP_AZ_CONTAINER_BG),
                        ...generateBorder(PALETTE.DESKTOP_AZ_BORDER),
                        overflow: 'hidden', // needed for round corners
                    },
                },
                style: {
                    header: {
                        style: {
                            ...resolveBackground(PALETTE.DESKTOP_AZ_HEADER_BG),
                            height: 40,
                            ...(PALETTE.DESKTOP_AZ_SHOW_HEADER_UNDERLINE
                                ? {
                                      borderWidth: '0 0 1px 0',
                                      borderColor: PALETTE.DESKTOP_AZ_HEADER_UNDERLINE_COLOR,
                                      borderStyle: 'solid',
                                  }
                                : undefined),
                        },
                    },
                    headerIcon: icon(PALETTE.DESKTOP_AZ_HEADER_ICON_COLOR),
                    headerText: {
                        style: PALETTE.DESKTOP_AZ_HEADER_TEXT,
                    },

                    separator: {
                        height: 0,
                        margin: 0,
                    },
                    toggleText: {
                        style: {
                            ...PALETTE.DESKTOP_AZ_EXPAND_BUTTON.TERTIARY_BUTTON_TEXT,
                        },
                    },
                },
                ...generateFavoriteIcon(PALETTE.DESKTOP_AZ_FAVORITE_ICON),
                sportItem: {
                    style: {
                        base: {
                            ...ITEMS_SEPARATOR_STYLE,
                            _mobile_margin: '0 8px',
                            _mobile_padding: '0 0 0 8px',
                            /**
                             * Setting `min-width: 0;` below is the hack to make `display: flex;` work with the `white-space: nowrap;`.
                             * Details: https://stackoverflow.com/a/38224283
                             */
                            minWidth: '0',
                            ...DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_TEXT,
                        },
                    },
                },
                sportIcon: {
                    container: {
                        style: {
                            flexBasis: 'auto',
                        },
                    },
                    icon: {
                        style: {
                            fill: DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_ICON_COLOR,
                        },
                    },
                },
                itemContainer: {
                    truncate: true,
                    container: {
                        flexBasis: 'auto',
                    },
                },
                rotation: {
                    image: {
                        style: {
                            fill: PALETTE.DESKTOP_AZ_EXPAND_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                            minHeight: '8px',
                            // minWidth: '8px' - by default
                        },
                    },
                },
            },
        };
    }

    getMobileConfig(PALETTE) {
        const {MOBILE_AZ_SPORTS_LIST} = PALETTE;

        const ITEMS_SEPARATOR_STYLE = MOBILE_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR
            ? {
                  borderWidth: '0 0 1px 0',
                  borderColor: MOBILE_AZ_SPORTS_LIST.LIST_ITEM_SEPARATOR_COLOR,
                  borderStyle: 'solid',
              }
            : undefined;

        return {
            // Used for both desktop and mobile. And there could be totally different styles
            SportsMenuView: {
                separator: {
                    show: MOBILE_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR,
                },
                style: {
                    separator: {
                        height: 0,
                        margin: 0,
                    },
                },
                ...generateFavoriteIcon(PALETTE.MOBILE_AZ_FAVORITE_ICON),
                sportItem: {
                    style: {
                        base: {
                            ...ITEMS_SEPARATOR_STYLE,
                            _mobile_margin: '0 8px',
                            _mobile_padding: '0 0 0 8px',
                            /**
                             * Setting `min-width: 0;` below is the hack to make `display: flex;` work with the `white-space: nowrap;`.
                             * Details: https://stackoverflow.com/a/38224283
                             */
                            minWidth: '0',
                            ...MOBILE_AZ_SPORTS_LIST.LIST_ITEM_TEXT,
                        },
                    },
                },
                sportIcon: {
                    container: {
                        style: {
                            flexBasis: 'auto',
                        },
                    },
                    icon: {
                        style: {
                            fill: MOBILE_AZ_SPORTS_LIST.LIST_ITEM_ICON_COLOR,
                        },
                    },
                },
                itemContainer: {
                    truncate: true,
                    container: {
                        flexBasis: 'auto',
                    },
                },
            },
            AppOverlay: {
                slidingAZMenu: {
                    slidingPane: {
                        style: {
                            childrenContainer: {
                                ...resolveBackground(PALETTE.MOBILE_AZ_CONTAINER_BG),
                            },
                        },
                        closeButtonContainer: {
                            style: {
                                right: 'calc(100% + 4px)',
                            },
                        },
                        closeButton: generateCloseButton(PALETTE.MOBILE_AZ_CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
                    },
                },
            },

            // In mojito, it is not possible to pass all settings menu configs to mobile AZMenu.
            // The only way to make it global
            ...generateSettingMenuGlobalStuff(PALETTE.MOBILE_AZ_SETTINGS),
            AZMenu: {
                settingsMenu: {
                    container: {
                        style: {
                            padding: '8px',
                        },
                    },
                },
                sequentialStateBackButtonLabel: {
                    style: {
                        ...PALETTE.MOBILE_AZ_BACK_BUTTON_TEXT,
                    },
                },
                sequentialStateBackButtonIcon: icon(PALETTE.MOBILE_AZ_BACK_BUTTON_ICON_COLOR),
            },
            AzOverview: {
                searchControllerView: {
                    appearance: 'search',
                    searchDiscoveryView: {
                        launcherButton: {
                            style: {
                                base: {
                                    justifyContent: 'flex-start',
                                },
                            },
                        },
                        launcherIcon: icon(PALETTE.MOBILE_AZ_DISCOVERY_SEARCH_LAUNCHER_ICON_COLOR),
                    },
                },
                container: {
                    nthSeparator: {
                        0: {
                            style: {color: 'transparent'}, // fix mojito default style
                        },
                    },
                    style: {
                        // Actually background is not needed here, as it always sits inside AppOverlay->slidingAZMenu->azMenu
                        // But leaving it here to show consistent mock-ups
                        ...resolveBackground(PALETTE.MOBILE_AZ_CONTAINER_BG),
                    },

                    separator: {
                        style: {
                            color: PALETTE.MOBILE_AZ_SECTION_SEPARATOR_COLOR,
                            size: PALETTE.MOBILE_AZ_SHOW_SECTION_SEPARATOR ? 1 : 0,
                        },
                    },
                },
                sectionName: {
                    container: {
                        padding: '8px 8px 8px 16px', // override mojito's wrong config
                        ...resolveBackground(PALETTE.MOBILE_AZ_SECTION_HEADER_CONTAINER_BG),
                        ...(PALETTE.MOBILE_AZ_SECTION_NAME_UNDERLINE
                            ? {
                                  //border: `1px solid ${PALETTE.DIVIDER_COLOR}`, <-- this not works (mojito magic)
                                  borderStyle: 'solid',
                                  borderWidth: '0 0 1px',
                                  borderColor: PALETTE.MOBILE_AZ_SECTION_SEPARATOR_COLOR,
                              }
                            : {}),
                    },
                    style: {
                        ...PALETTE.MOBILE_AZ_SECTION_HEADER_TEXT,
                    },
                },
                sportsSectionAllSportsButton: {
                    style: {
                        base: {
                            padding: '0 16px', // override mojito's wrong config
                            ...(PALETTE.MOBILE_AZ_ALL_SPORTS_BUTTON_OVERLINE
                                ? {
                                      borderStyle: 'solid',
                                      borderWidth: '1px 0 0',
                                      borderColor: PALETTE.MOBILE_AZ_SECTION_SEPARATOR_COLOR,
                                  }
                                : {}),
                            ...PALETTE.MOBILE_AZ_ALL_SPORTS_BUTTON.TERTIARY_BUTTON_TEXT,
                        },
                        //PALETTE.ALL_SPORTS_BUTTON_TEXT || PALETTE.TERTIARY_BUTTON_TEXT
                    },
                },
                sportsSectionAllSportsButtonIcon: merge(
                    {
                        style: {
                            padding: 8, //fix position according to text line
                        },
                    },
                    icon(PALETTE.MOBILE_AZ_ALL_SPORTS_BUTTON.TERTIARY_BUTTON_ICON_COLOR)
                ),
                informationSectionContainer: merge(generateLinkMenuVerticalList(PALETTE.MOBILE_AZ_INFORMATION_LIST), {
                    regularItem: {
                        // selected: {
                        //     style: {
                        //         base: {
                        //             _mobile_margin: '0 8px',
                        //             padding: 8, // override mojito's wrong config
                        //             _mobile_padding: 8,
                        //         },
                        //     },
                        // },
                        unselected: {
                            style: {
                                base: {
                                    _mobile_margin: '0 8px',
                                    padding: 8, // override mojito's wrong config
                                    _mobile_padding: '0 0 0 8px',
                                },
                            },
                        },
                    },
                }),
                informationSectionItems: buildIntentsFromConfig(ApplicationConfig.config.mobileInfoSectionItems),
                moreSectionItems: buildIntentsFromConfig(ApplicationConfig.config.mobileMoreSectionItems),
                moreSection: {
                    icon: {
                        style: {
                            width: 16,
                            height: 16,
                            minHeight: 16,
                            fill: PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_ICON_COLOR,
                        },
                    },
                    compactItem: {
                        unselected: {
                            style: {
                                base: {
                                    margin: '0 8px',
                                    padding: '0 0 0 8px', // override mojito's default
                                    alignItems: 'center',
                                    ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                                    ...ITEMS_SEPARATOR_STYLE,
                                },
                            },
                        },
                        selected: {
                            style: {
                                base: {
                                    margin: '0 8px',
                                    padding: '0 0 0 8px', // override mojito's default
                                    alignItems: 'center',
                                    ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                                },
                            },
                        },
                    },
                    regularItem: {
                        unselected: {
                            style: {
                                base: {
                                    margin: '0 8px',
                                    padding: '0 0 0 8px', // override mojito's default
                                    alignItems: 'center',
                                    ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                                    ...ITEMS_SEPARATOR_STYLE,
                                },
                            },
                        },
                        selected: {
                            style: {
                                base: {
                                    margin: '0 8px',
                                    padding: '0 0 0 8px', // override mojito's default
                                    alignItems: 'center',
                                    ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                                },
                            },
                        },
                    },
                },
            },
        };
    }

    getSerializableVisualConfig(PALETTE) {
        const result = isMobile() ? this.getMobileConfig(PALETTE) : this.getDesktopConfig(PALETTE);

        return mergeTo(result, {
            SportsMenu: {
                numberOfTopSportsToShow: this.config.numberOfTopSports,
                sportsMenuView: {
                    highlightedSports: {
                        maxAmount: this.config.numberOfTopSports,
                    },
                },
            },
        });
    }
}

new AZMenuFeature(MenusLinksLists);
